import { AudioVideoFacade, DefaultBrowserBehavior } from "amazon-chime-sdk-js";
import isEqual from "lodash.isequal";
import React, { ReactNode, useEffect, useState } from "react";
import {
  useToggleLocalMute,
  useAudioVideo,
  useLogger,
  useMeetingManager,
} from "amazon-chime-sdk-component-library-react";
import { useDevices } from "../../../video/providers/DevicesProvider";
import { Mic, MicOff } from "lucide-react";
import { DropdownButtonItem } from "@shared/ui/DropdownButtonItem";
import { ButtonDropdownCombo } from "@shared/ui/ButtonDropdownCombo";
import { Type } from "@shared/ui/Type";
import { DeviceType } from "../../../video/types";
import { isOptionActive } from "../../../video/utils/device-utils";
import useMemoCompare from "../../../video/utils/use-memo-compare";
import { VideoUIContainerBreakpoints } from "../../videoUI.types";
import { videoEventBus } from "../../../video/videoEventBus";
import { useBrowserCompatibilityFeatures } from "../../../video/hooks/useBrowserCompatibilityFeatures";

interface Props {
  muteLabel?: string;
  unmuteLabel?: string;
  voiceFocusOnLabel?: string;
  voiceFocusOffLabel?: string;
  containerBreakpoints: VideoUIContainerBreakpoints;
}

export const AudioInputVFControl: React.FC<React.PropsWithChildren<Props>> = ({
  muteLabel = "Mute",
  unmuteLabel = "Unmute",
  voiceFocusOnLabel = "Background noise reduction enabled",
  voiceFocusOffLabel = "Enable background noise reduction",
  containerBreakpoints,
}) => {
  const logger = useLogger();

  const audioVideo = useAudioVideo();

  const meetingManager = useMeetingManager();

  const [isLoading, setIsLoading] = useState(false);

  // Voice Focus state removed - no longer using background noise reduction

  const [dropdownWithVFOptions, setDropdownWithVFOptions] = useState<
    ReactNode[] | null
  >(null);

  const { muted, toggleMute } = useToggleLocalMute();

  // Voice Focus hooks removed - no longer using background noise reduction

  const {
    audioInputDevices: devices,
    selectedAudioInputDevice: selectedDevice,
    audioOutputDevices,
    selectedAudioOutputDevice: audioOutputDevice,
    selectAudioInputDevice,
    selectAudioOutputDevice,
  } = useDevices();

  // Get browser compatibility features
  const { supportsAudio, supportsAudioOutput } =
    useBrowserCompatibilityFeatures();

  const audioInputDevices: DeviceType[] = useMemoCompare(
    devices,
    (prev: DeviceType[], next: DeviceType[]): boolean => {
      return isEqual(prev, next);
    }
  );

  // Voice Focus effects removed - no longer using background noise reduction

  useEffect(() => {
    const handleClickAudioOutputDevice = async (
      deviceId: string
    ): Promise<void> => {
      try {
        if (new DefaultBrowserBehavior().supportsSetSinkId()) {
          // Update our custom device provider
          await selectAudioOutputDevice(deviceId);
          // Also update the Chime SDK
          await meetingManager.startAudioOutputDevice(deviceId);
        } else {
          logger.error(
            "AudioOutputControl cannot select audio output device because browser does not support setSinkId operation."
          );
        }
      } catch (error) {
        logger.error("AudioOutputControl failed to select audio output device");
      }
    };

    const handleClick = async (deviceId: string): Promise<void> => {
      setIsLoading(true);
      try {
        // Update our custom device provider
        await selectAudioInputDevice(deviceId);

        // Start the audio input device directly (no Voice Focus)
        await meetingManager.startAudioInputDevice(deviceId);

        videoEventBus.emitEvent("videochat:audio:source:selected", {
          deviceId,
        });
      } catch (error) {
        logger.error("AudioInputVFControl failed to select audio input device");
      } finally {
        setIsLoading(false);
      }
    };

    const getDropdownWithVFOptions = async (): Promise<void> => {
      let dropdownOptions = [];

      const dropdownOptionsAudioInput: ReactNode[] = await Promise.all(
        audioInputDevices.map(async (device) => (
          <DropdownButtonItem
            key={`audio-input-${device.deviceId}`}
            checked={await isOptionActive(selectedDevice, device.deviceId)}
            onClick={async () => await handleClick(device.deviceId)}
            disabled={isLoading}
          >
            <span>{device.label}</span>
          </DropdownButtonItem>
        ))
      );

      const dropdownOptionsAudioOutput: ReactNode[] = await Promise.all(
        audioOutputDevices.map(async (device) => (
          <DropdownButtonItem
            key={`audio-output-${device.deviceId}`}
            checked={await isOptionActive(audioOutputDevice, device.deviceId)}
            onClick={async () =>
              await handleClickAudioOutputDevice(device.deviceId)
            }
          >
            <span>{device.label}</span>
          </DropdownButtonItem>
        ))
      );

      // Voice Focus option removed - no longer creating background noise reduction toggle

      if (supportsAudioOutput) {
        dropdownOptions.push(
          <Type
            variant="h6"
            className="text-brand-600 px-4 py-4 border-brand-800"
            key="audio-output"
          >
            Audio Output
          </Type>,
          ...dropdownOptionsAudioOutput
        );
      }

      if (supportsAudio) {
        dropdownOptions.push(
          <Type
            variant="h6"
            className="text-brand-600 px-4 py-4 border-brand-800"
            key="audio-input"
          >
            Audio Input
          </Type>,
          ...dropdownOptionsAudioInput
        );
      }

      // Voice Focus options removed - no longer showing background noise reduction toggle

      setDropdownWithVFOptions(dropdownOptions);
    };

    getDropdownWithVFOptions();
  }, [
    // The contents of this dropdown depends on the current selected device and available devices
    meetingManager,
    meetingManager.startAudioInputDevice,
    audioInputDevices,
    isLoading,
    selectedDevice,
    selectAudioInputDevice,
    selectAudioOutputDevice,
  ]);

  // Voice Focus checkbox change effect removed - no longer using background noise reduction

  const handleToggleMute = () => {
    // Track the event BEFORE toggling (based on current state)
    if (muted) {
      videoEventBus.emitEvent("videochat:unmuted", {});
    } else {
      videoEventBus.emitEvent("videochat:muted", {});
    }

    // Then toggle the mute state
    toggleMute();
  };

  return (
    <ButtonDropdownCombo
      buttonIcon={muted ? MicOff : Mic}
      buttonLabel={
        containerBreakpoints && containerBreakpoints.isMd
          ? muted
            ? unmuteLabel
            : muteLabel
          : ""
      }
      buttonOnClick={supportsAudio ? handleToggleMute : () => {}}
      dropdownOptions={supportsAudio ? dropdownWithVFOptions || [] : []}
      destructive={muted}
      disabled={!supportsAudio}
    />
  );
};

export default AudioInputVFControl;
